import React from "react";
import { FilterPanel } from "./filter-panel";
import { Filter, CloudDownload, Search } from "lucide-react";
import { FilterCondition, FilterConfig } from "@/interfaces/filter.interface";
import { cn } from "@/lib/utils";
import { Input } from "../ui/input";

/**
 * Props for the FilterBar component
 */
interface FilterBarProps {
  /** Whether the filter panel is open */
  isFilterOpen: boolean;
  /** Callback fired when filter toggle button is clicked */
  onFilterToggle: () => void;
  /** Callback fired when filter panel is closed */
  onFilterClose?: () => void;
  /** Array of filter conditions */
  conditions: FilterCondition[];
  /** Filter configuration object */
  config: FilterConfig;
  /** Callback fired when a condition changes */
  onConditionChange: (condition: FilterCondition) => void;
  /** Callback fired when a condition is removed */
  onConditionRemove: (conditionId: string) => void;
  /** Callback fired when a condition is added */
  onConditionAdd: () => void;
  /** Callback fired when pending filter changes */
  onPendingFilterChange?: (pending: Partial<FilterCondition> | null) => void;
  /** Callback fired when CSV download is requested */
  onDownloadCSV?: () => void;
  /** Current search input value */
  searchValue?: string;
  /** Callback fired when search value changes */
  onSearchChange?: (text: string) => void;
  /** Additional CSS classes */
  className?: string;
}

/**
 * FilterBar component displays a filter button, optional search input, and CSV download
 * button. Manages the filter panel visibility and delegates filter operations to FilterPanel.
 * 
 * @component
 * @param {FilterBarProps} props - Component props
 * @param {boolean} props.isFilterOpen - Whether filter panel is open
 * @param {() => void} props.onFilterToggle - Toggle callback
 * @param {() => void} [props.onFilterClose] - Close callback
 * @param {FilterCondition[]} props.conditions - Filter conditions
 * @param {FilterConfig} props.config - Filter configuration
 * @param {(condition: FilterCondition) => void} props.onConditionChange - Condition change callback
 * @param {(conditionId: string) => void} props.onConditionRemove - Condition remove callback
 * @param {() => void} props.onConditionAdd - Condition add callback
 * @param {(pending: Partial<FilterCondition> | null) => void} [props.onPendingFilterChange] - Pending filter change callback
 * @param {() => void} [props.onDownloadCSV] - CSV download callback
 * @param {string} [props.searchValue] - Search input value
 * @param {(text: string) => void} [props.onSearchChange] - Search change callback
 * @param {string} [props.className] - Additional CSS classes
 * @returns {JSX.Element} Filter bar with toggle button, search, and download
 * 
 * @example
 * ```tsx
 * <FilterBar
 *   isFilterOpen={isOpen}
 *   onFilterToggle={handleToggle}
 *   conditions={filters}
 *   config={filterConfig}
 *   onConditionChange={handleChange}
 * />
 * ```
 */
export const FilterBar: React.FC<FilterBarProps> = ({
  isFilterOpen,
  onFilterToggle,
  onFilterClose,
  conditions,
  config,
  onConditionChange,
  onConditionRemove,
  onConditionAdd,
  onPendingFilterChange,
  onDownloadCSV,
  searchValue,
  onSearchChange,
  className,
}) => {
  return (
    <div className={cn("space-y-4", className)}>
      {/* Filter + Search on left, CSV on right */}
      <div className="flex items-center justify-between">
        {/* Filter button + Search */}
        <div className="flex items-center gap-3">
          {/* Filter Button */}
          <button
            type="button"
            onClick={onFilterToggle}
            className={cn(
              "flex items-center rounded-md pr-3 pl-1 transition-colors",
              isFilterOpen
                ? "bg-primary-500 text-white hover:bg-primary-600"
                : "bg-white border border-gray-200 hover:bg-gray-50 text-gray-700"
            )}
            aria-label="Toggle filter panel"
          >
            <div
              className={cn(
                "w-8 h-8 rounded-full flex items-center justify-center",
                isFilterOpen
                  ? "bg-primary-500 text-white"
                  : "bg-white text-gray-600"
              )}
            >
              <Filter className="h-4 w-4" />
            </div>
            <div className={cn(
                "flex items-center justify-center pr-2 font-primary",
                isFilterOpen ? "text-white" : "text-gray-700"
            )}>
              Filter
            </div>
          </button>

          {/* search bar */}
          {onSearchChange && (
            <div className="relative w-[30rem]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 h-4 w-4" />
              <Input
                value={searchValue}
                onChange={(e) => onSearchChange(e.target.value)}
                placeholder="Search Users"
                className="pl-10 w-full"
              />
            </div>
          )}
        </div>

        {onDownloadCSV && (
          <button
            type="button"
            onClick={onDownloadCSV}
            className="flex items-center gap-2 text-primary-500 hover:text-primary-600 transition-colors bg-transparent border-none p-0 cursor-pointer"
            aria-label="Download as CSV"
          >
            <CloudDownload className="h-4 w-4" />
            <span className="font-primary">Download as CSV</span>
          </button>
        )}
      </div>

      {/* Filter Panel */}
      <FilterPanel
        isOpen={isFilterOpen}
        conditions={conditions}
        config={config}
        onConditionChange={onConditionChange}
        onConditionRemove={onConditionRemove}
        onConditionAdd={onConditionAdd}
        onPendingFilterChange={onPendingFilterChange}
        onClose={onFilterClose || onFilterToggle}
      />
    </div>
  );
};

